import { InvoiceWithPostings, Posting, Transaction, TransactionWithoutPostings } from '@rentguru/commons-utils';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import uniq from 'lodash/uniq';

import { fetchInvoices } from 'src/hooks/InvoicesContext';
import { fetchPostings } from 'src/hooks/PostingsContext';
import { fetchTransaction, fetchTransactionIfExists } from 'src/hooks/TransactionsContext';

import {
  attachLeasePostingsToInvoices,
  attachLeasePostingsToTransactions,
  getOffsetCounterpartIds,
  getTransactionIdsMissingFromLease,
  LeaseInvoicesAndPostings,
  mergeFetchedLeaseTransactions,
  replaceLeaseInvoices,
  toLoadedLeaseInvoices,
} from './detailedReconciliationUtils';

export interface LeaseReconciliationData {
  invoices: InvoiceWithPostings[];
  transactions: (TransactionWithoutPostings & { postings: Posting[] })[];
  leasePostings: Posting[];
}

export interface LeaseReconciliationSnapshot extends LeaseInvoicesAndPostings {
  transactions: TransactionWithoutPostings[];
}

// Payments settled on an early draft only carry DRAFT postings until the draft is posted
const DRAFT_POSTINGS_INCLUDED = { includesDraftPostings: true };

const fetchLeasePostings = (leaseId: string): Promise<Posting[]> =>
  fetchPostings('byLease', leaseId, undefined, undefined, DRAFT_POSTINGS_INCLUDED);

const loadLeaseTransactions = async (
  leaseId: string,
  leasePostings: Posting[],
  leaseTransactions: TransactionWithoutPostings[],
  transactionIdsToRefresh: string[]
): Promise<TransactionWithoutPostings[]> => {
  const transactionIdsToFetch = uniq([
    ...transactionIdsToRefresh,
    ...getTransactionIdsMissingFromLease(leasePostings, leaseTransactions),
  ]);
  const fetchedTransactions = await Promise.all(
    transactionIdsToFetch.map(async (id) => {
      const transaction = await fetchTransactionIfExists(id);
      return { id, transaction: isNil(transaction) ? null : omit(transaction, 'postings') };
    })
  );
  return mergeFetchedLeaseTransactions(leaseTransactions, fetchedTransactions, leaseId);
};

export const fetchLeaseReconciliationSnapshot = async (
  leaseId: string,
  leaseTransactions: TransactionWithoutPostings[],
  transactionIdsToRefresh: string[] = []
): Promise<LeaseReconciliationSnapshot> => {
  const [leaseInvoices, leasePostings] = await Promise.all([
    fetchInvoices('byLease', leaseId),
    fetchLeasePostings(leaseId),
  ]);
  return {
    ...toLoadedLeaseInvoices(leaseInvoices, leaseId),
    leasePostings,
    transactions: await loadLeaseTransactions(leaseId, leasePostings, leaseTransactions, transactionIdsToRefresh),
  };
};

export const refreshSnapshotPostings = async (
  leaseId: string,
  snapshot: LeaseReconciliationSnapshot,
  leaseTransactions: TransactionWithoutPostings[]
): Promise<LeaseReconciliationSnapshot> => {
  const leasePostings = await fetchLeasePostings(leaseId);
  return {
    ...snapshot,
    leasePostings,
    transactions: await loadLeaseTransactions(leaseId, leasePostings, leaseTransactions, []),
  };
};

export const refreshSnapshotInvoices = async (
  leaseId: string,
  snapshot: LeaseReconciliationSnapshot,
  invoiceIds: string[]
): Promise<LeaseReconciliationSnapshot> => {
  const refreshedIds = new Set(invoiceIds);
  const [leaseInvoices, ...refreshedPostings] = await Promise.all([
    fetchInvoices('byLease', leaseId),
    ...invoiceIds.map((invoiceId) =>
      fetchPostings('byInvoice', invoiceId, undefined, undefined, DRAFT_POSTINGS_INCLUDED)
    ),
  ]);
  return {
    ...snapshot,
    ...replaceLeaseInvoices(
      snapshot,
      leaseId,
      invoiceIds,
      leaseInvoices.filter(({ id }) => refreshedIds.has(id)),
      refreshedPostings.flat()
    ),
  };
};

export const toLeaseReconciliationData = ({
  leaseInvoices,
  leasePostings,
  transactions,
}: LeaseReconciliationSnapshot): LeaseReconciliationData => ({
  invoices: attachLeasePostingsToInvoices(leaseInvoices, leasePostings),
  transactions: attachLeasePostingsToTransactions(transactions, leasePostings),
  leasePostings,
});

export const fetchTransactionsWithAllPostings = async <SelectedTransaction extends Pick<Transaction, 'id'>>(
  transactions: SelectedTransaction[]
): Promise<SelectedTransaction[]> =>
  Promise.all(
    transactions.map(async (transaction) => {
      const freshTransaction = await fetchTransaction(transaction.id);
      return { ...transaction, ...omit(freshTransaction, 'bankAccount') };
    })
  );

export const withFreshOffsetCounterparts = async <OffsetTransaction extends Pick<Transaction, 'id'>>(
  transaction: Pick<Transaction, 'postings'>,
  transactions: OffsetTransaction[]
): Promise<OffsetTransaction[]> => {
  const counterpartIds = getOffsetCounterpartIds(transaction.postings ?? []);
  const freshCounterparts = await fetchTransactionsWithAllPostings(
    transactions.filter(({ id }) => counterpartIds.includes(id))
  );
  return transactions.map(
    (currentTransaction) => freshCounterparts.find(({ id }) => id === currentTransaction.id) ?? currentTransaction
  );
};
